import { FC, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Box, Flex, HStack, SimpleGrid, Spacer } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';

import { TransactionPaymentMethod, TransactionStatus } from '__generated__/GQL';
import dateToString from 'common/dateToString';
import useFeatureFlagsSwitcher from 'common/hooks/useFeatureFlagsSwitcher';
import Button from 'components/_core/Button';
import Chip from 'components/_core/Chip';
import * as Form from 'components/_core/form';
import Text from 'components/_core/Text';
import { logEvent } from 'features/Analytics/analytics';

import {
  defaults,
  useTransactionsFilters,
} from '../../../../hooks/useTransactionsFilters';
import { formSchema, SchemaOutput } from './advancedFilters.schema';
import locales from './locales';

const { q: defaultQ, ...defaultFilters } = defaults;

interface AdvancedFiltersProps {
  totalCount: number;
}

const AdvancedFilters: FC<AdvancedFiltersProps> = ({ totalCount }) => {
  const { featureFlags } = useFeatureFlagsSwitcher();
  const {
    // We ignore the search property because it isn't included in the form
    filters: { q, ...filters },
    updateFilters,
  } = useTransactionsFilters();

  const {
    control,
    formState: { errors },
    getValues,
    handleSubmit,
    reset,
    watch,
  } = useForm<SchemaOutput>({
    defaultValues: filters,
    mode: 'onChange',
    resolver: zodResolver(formSchema),
    shouldFocusError: false,
  });

  const paymentMethodOptions = Object.values(TransactionPaymentMethod)
    .filter((method) => {
      if (method === TransactionPaymentMethod.SwiftTransfer) {
        return featureFlags.enableSwiftTransfer;
      }
      return true;
    })
    .map((paymentMethod) => ({
      label: locales.paymentMethod[paymentMethod],
      value: paymentMethod,
    }));

  const statusOptions = Object.values(TransactionStatus).map((status) => ({
    label: locales.status[status],
    value: status,
  }));

  const onSubmit = useMemo(
    () => handleSubmit(updateFilters),
    [handleSubmit, updateFilters],
  );

  useEffect(() => {
    // Watch for field changes.
    const subscription = watch(() => onSubmit());
    return () => subscription.unsubscribe();
  }, [watch, onSubmit]);

  return (
    <Box
      as="form"
      border="1px solid"
      borderBottomRadius="radius-6"
      borderColor="input-borderColor"
      borderTop="0"
      onSubmit={onSubmit}
      padding="space-24"
    >
      <SimpleGrid
        columns={{ '2xl': 2, '3xl': 3, base: 1 }}
        marginBottom={{ '2xl': 'space-24', base: 'space-16' }}
        spacingX={{ '3xl': 'space-32', base: 'space-24' }}
        spacingY={{ '2xl': 'space-24', base: 'space-16' }}
      >
        <Form.Field flex={1} label={locales.operations.label}>
          <HStack spacing="space-8">
            <Controller
              control={control}
              name="payin"
              render={({ field: { onChange, value } }) => (
                <Chip
                  flex={1}
                  iconRight="transaction-in"
                  isActive={value}
                  onClick={() => onChange(!value)}
                >
                  {locales.operations.payin}
                </Chip>
              )}
            />

            <Controller
              control={control}
              name="payout"
              render={({ field: { onChange, value } }) => (
                <Chip
                  flex={1}
                  iconRight="transaction-out"
                  isActive={value}
                  onClick={() => onChange(!value)}
                >
                  {locales.operations.payout}
                </Chip>
              )}
            />
          </HStack>
        </Form.Field>

        <Form.Field
          error={
            errors.valueLesserThan?.message || errors.valueGreaterThan?.message
          }
          flex={1}
          label={locales.amounts.label}
        >
          <HStack spacing="space-8">
            <Controller
              control={control}
              name="valueGreaterThan"
              render={({ field: { value, ...field } }) => (
                <Form.Input
                  {...field}
                  flex={1}
                  name="valueGreaterThan"
                  placeholder={locales.amounts.greaterThanPlaceholder}
                  rightIcon="euro"
                  type="number"
                  value={value ?? ''}
                />
              )}
            />

            <Text variant="light">{locales.amounts.to}</Text>

            <Controller
              control={control}
              name="valueLesserThan"
              render={({ field: { value, ...field } }) => (
                <Form.Input
                  {...field}
                  flex={1}
                  name="valueLesserThan"
                  placeholder={locales.amounts.lesserThanPlaceholder}
                  rightIcon="euro"
                  type="number"
                  value={value ?? ''}
                />
              )}
            />
          </HStack>
        </Form.Field>

        <Form.Field flex={1} label={locales.dates.label}>
          <HStack spacing="space-8">
            <Controller
              control={control}
              name="afterDate"
              render={({ field: { onChange, value } }) => {
                const beforeDate = getValues('beforeDate');

                return (
                  <Form.SunshineInputDatePicker
                    inputProps={{
                      placeholder: locales.dates.beforeDatePlaceholder,
                    }}
                    modifiers={{
                      ...(beforeDate && {
                        disabled: {
                          after: new Date(beforeDate),
                        },
                      }),
                    }}
                    onChange={(date) =>
                      onChange(date ? dateToString(date) : undefined)
                    }
                    value={value ? new Date(value) : undefined}
                  />
                );
              }}
            />

            <Text variant="light">{locales.dates.to}</Text>

            <Controller
              control={control}
              name="beforeDate"
              render={({ field: { onChange, value } }) => {
                const afterDate = getValues('afterDate');

                return (
                  <Form.SunshineInputDatePicker
                    inputProps={{
                      placeholder: locales.dates.beforeDatePlaceholder,
                    }}
                    modifiers={{
                      ...(afterDate && {
                        disabled: {
                          before: new Date(afterDate),
                        },
                      }),
                    }}
                    onChange={(date) =>
                      onChange(date ? dateToString(date) : undefined)
                    }
                    value={value ? new Date(value) : undefined}
                  />
                );
              }}
            />
          </HStack>
        </Form.Field>

        <Form.Field flex={1} label={locales.status.label}>
          <Controller
            control={control}
            name="status"
            render={({ field: { onChange, value } }) => (
              <Form.SunshineSelect
                isMulti
                onChange={(options) =>
                  onChange(options ? options.map((option) => option.value) : [])
                }
                options={statusOptions}
                placeholder={locales.status.placeholder}
                value={statusOptions.filter((option) =>
                  value.includes(option.value),
                )}
              />
            )}
          />
        </Form.Field>

        <Form.Field flex={1} label={locales.paymentMethod.label}>
          <Controller
            control={control}
            name="paymentMethod"
            render={({ field: { onChange, value } }) => (
              <Form.SunshineSelect
                isMulti
                onChange={(options) =>
                  onChange(options ? options.map((option) => option.value) : [])
                }
                options={paymentMethodOptions}
                placeholder={locales.paymentMethod.placeholder}
                value={paymentMethodOptions.filter((option) =>
                  value.includes(option.value),
                )}
              />
            )}
          />
        </Form.Field>

        <Form.Field flex={1} label={locales.accounting.label}>
          <Controller
            control={control}
            name="withoutReceipts"
            render={({ field: { onChange, value } }) => (
              <Chip
                icon="paper-clip"
                isActive={value}
                onClick={() => onChange(!value)}
                width="full"
              >
                {locales.accounting.withoutReceipts}
              </Chip>
            )}
          />
        </Form.Field>
      </SimpleGrid>

      <Flex
        alignItems="center"
        display="flex"
        flexDirection={{
          base: 'column-reverse',
          xl: 'row',
        }}
        justifyContent="space-between"
        paddingX={{ base: 'space-24', xl: 'space-0' }}
      >
        {totalCount > 0 ? (
          <Text>
            {locales.formatString(
              totalCount === 1
                ? locales.totalCountSingular
                : locales.totalCountPlural,
              totalCount,
            )}
          </Text>
        ) : null}

        <Spacer />

        <Button
          iconPosition="right"
          marginBottom={{ base: 'space-16', xl: 'space-0' }}
          onClick={() => {
            logEvent({
              name: 'Transactions Filters Cleared',
              usePrefix: false,
            });
            reset(defaultFilters);
          }}
          variant="discreet"
          width={{ base: 'full', xl: 'auto' }}
        >
          {locales.resetFilters}
        </Button>
      </Flex>
    </Box>
  );
};

export default AdvancedFilters;
